import React, { useEffect, useMemo, useRef, useState } from "react"
import PPLoadingIndicator from "../../components/layout/PPLoadingIndicator"
import PPErrorDisplay from "../../components/layout/PPErrorDisplay"
import Collapse from "../../components/layout/PPCollapse"
import { useQuery } from "@apollo/client"
import PPDonutStatsBox from "../../components/dashboard/PPDonutStatsBox"
import PPTopProducts from "../../components/dashboard/PPTopProducts"
import PPStatTableCountPriceLower from "../../components/dashboard/PPStatTableCountPriceLower"
import PPPriceTrendStatistics from "../../components/dashboard/PPPriceTrendStatistics"
import PPUtils from "../../utils/PPUtils"
import { useTranslation } from "react-i18next"
import PPUtilsCatFilter from "../../utils/PPUtilsCatFilter"
import PPTripleBarStatsBox from "../../components/dashboard/PPTripleBarStatsBox"
import { PPDefaultSelector } from "../../components/ui/PPDefaultSelector"
import "../../styles/dashboard.css"
import { QUERY, queryActiveShopInfos } from "./dashboardQueries"
import { ShopFilterMappingHistoryChart } from "./MappingHistoryChart"
import { PPStatsBox, DetailStatsPrices, DetailStatsShops, DetailStatsCompetitorMostPriceChanges, DetailStatsCompetitorLowestPrice } from "./statsBoxes"

const EMPTY_SHOPS = []

function PPDashboard() {
    const [t] = useTranslation()

    const [shopStatsOpen, setShopStatsOpen] = useState(false)
    const [lowerPricesOpen, setLowerPricesOpen] = useState(false)
    const [equalPricesOpen, setEqualPricesOpen] = useState(false)
    const [higherPricesOpen, setHigherPricesOpen] = useState(false)
    const [competitorStatsMostPriceChangesOpen, setCompetitorStatsMostPriceChangesOpen] = useState(false)
    const [competitorStatsLowestPriceOpen, setCompetitorStatsLowestPriceOpen] = useState(false)
    const [querydataAll, setQuerydataAll] = useState()
    const [selectedDepartment, setSelectedDepartment] = useState(null)

    const variables = useMemo(
        () => ({
            department: selectedDepartment ? selectedDepartment.value : ""
        }),
        [selectedDepartment]
    )

    const { data: querydata, error } = useQuery(QUERY, {
        displayName: "DashboardQuery",
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "cache-first",
        variables
    })

    const { data: querydataShopInfos, error: errorShopInfos } = useQuery(queryActiveShopInfos, {
        displayName: "DashboardQuery2",
        fetchPolicy: "cache-and-network"
    })

    const prevQuerydataRef = useRef()
    useEffect(() => {
        if (error) console.error(error)
    }, [error])
    useEffect(() => {
        if (errorShopInfos) console.error(errorShopInfos)
    }, [errorShopInfos])
    useEffect(() => {
        if (!querydata) return
        if (selectedDepartment == null || selectedDepartment.value == "") {
            setQuerydataAll((prev) => (Array.isArray(prev) && prev.length === 0 ? prev : []))
        } else {
            setQuerydataAll(prevQuerydataRef.current)
        }
        prevQuerydataRef.current = querydata
    }, [querydata, selectedDepartment])

    const tariffComparisonMode = querydata?.baseData?.tariffComparisonMode ?? "N"
    const demoMode = !!querydata?.baseData?.demoMode
    const categorieTree = useMemo(() => {
        const cats = querydata?.baseData?.categories
        if (!cats) return []
        return [PPUtilsCatFilter.getEmptyObject(), ...cats]
    }, [querydata])

    const TAB_NAME_KATEGORIE = t("single.category")
    const TAB_NAME_HERSTELLER = PPUtils.isTariffComparisonMode(tariffComparisonMode)
        ? PPUtils.getTariffComparisonModeProducerString(t)
        : t("single.manufacturer")
    const TAB_NAME_SHOP = "Shop"

    useEffect(() => {
        let alreadyShown = localStorage.getItem("demo_popup_shown")
        if(!alreadyShown && demoMode) {
            const modalEl = document.getElementById("demoNoteModalCenter")
            if (modalEl && window.bootstrap?.Modal) {
                window.bootstrap.Modal.getOrCreateInstance(modalEl).show()
            }
            localStorage.setItem("demo_popup_shown", true)
        }
    }, [demoMode])

    function closeDemoNoteModal() {
        const modalEl = document.getElementById("demoNoteModalCenter")
        if (modalEl && window.bootstrap?.Modal) {
            window.bootstrap.Modal.getOrCreateInstance(modalEl).hide()
        }
    }

    function showMoreClick(clickedComponent) {
        if (clickedComponent === "activeShops") {
            setShopStatsOpen(!shopStatsOpen)
            setLowerPricesOpen(false)
            setHigherPricesOpen(false)
            setEqualPricesOpen(false)
            setCompetitorStatsMostPriceChangesOpen(false)
            setCompetitorStatsLowestPriceOpen(false)
        } else if (clickedComponent === "lowerPrices") {
            setLowerPricesOpen(!lowerPricesOpen)
            setHigherPricesOpen(false)
            setEqualPricesOpen(false)
            setShopStatsOpen(false)
            setCompetitorStatsMostPriceChangesOpen(false)
            setCompetitorStatsLowestPriceOpen(false)
        } else if (clickedComponent === "equalsPrices") {
            setEqualPricesOpen(!equalPricesOpen)
            setLowerPricesOpen(false)
            setHigherPricesOpen(false)
            setShopStatsOpen(false)
            setCompetitorStatsMostPriceChangesOpen(false)
            setCompetitorStatsLowestPriceOpen(false)
        } else if (clickedComponent === "higherPrices") {
            setHigherPricesOpen(!higherPricesOpen)
            setLowerPricesOpen(false)
            setEqualPricesOpen(false)
            setShopStatsOpen(false)
            setCompetitorStatsMostPriceChangesOpen(false)
            setCompetitorStatsLowestPriceOpen(false)
        } else if (clickedComponent === "topCompetitorPriceChanges") {
            setCompetitorStatsMostPriceChangesOpen(!competitorStatsMostPriceChangesOpen)
            setHigherPricesOpen(false)
            setLowerPricesOpen(false)
            setEqualPricesOpen(false)
            setShopStatsOpen(false)
            setCompetitorStatsLowestPriceOpen(false)
        } else if (clickedComponent === "topCompetitorLowestPrice") {
            setCompetitorStatsLowestPriceOpen(!competitorStatsLowestPriceOpen)
            setHigherPricesOpen(false)
            setLowerPricesOpen(false)
            setEqualPricesOpen(false)
            setShopStatsOpen(false)
            setCompetitorStatsMostPriceChangesOpen(false)
        }
    }

    const isTotalPriceComparison = localStorage.getItem("showTotalPrice") === "true"
    const stats = querydata?.stats
    const statsAll = querydataAll?.stats
    const pickStat = (obj, key) => (isTotalPriceComparison ? obj?.[`${key}Total`] : obj?.[key])
    const donutLower = pickStat(stats, "productsPriceLower")
    const donutEqual = pickStat(stats, "productsPriceEqual")
    const donutHigher = pickStat(stats, "productsPriceHigher")
    const donutLowerAll = pickStat(statsAll, "productsPriceLower")
    const donutEqualAll = pickStat(statsAll, "productsPriceEqual")
    const donutHigherAll = pickStat(statsAll, "productsPriceHigher")
    const lowerByCategory = pickStat(stats, "productsPriceLowerByCategory") || []
    const equalByCategory = pickStat(stats, "productsPriceEqualByCategory") || []
    const higherByCategory = pickStat(stats, "productsPriceHigherByCategory") || []
    const lowerByProducer = pickStat(stats, "productsPriceLowerByProducer") || []
    const equalByProducer = pickStat(stats, "productsPriceEqualByProducer") || []
    const higherByProducer = pickStat(stats, "productsPriceHigherByProducer") || []
    const lowerByShops = pickStat(stats, "productsPriceLowerByShops") || []
    const equalByShops = pickStat(stats, "productsPriceEqualByShops") || []
    const higherByShops = pickStat(stats, "productsPriceHigherByShops") || []
    const sortLower = isTotalPriceComparison ? "foreignShopTotalPriceLower" : "foreignShopPriceLower"
    const sortEqual = isTotalPriceComparison ? "foreignShopTotalPriceEqual" : "foreignShopPriceEqual"
    const sortHigher = isTotalPriceComparison ? "foreignShopTotalPriceHigher" : "foreignShopPriceHigher"

    if (!querydata && !error) {
        return <PPLoadingIndicator />
    }

    return (
        <div>
            {error ? (
                <div className="row">
                    <div className="col-12">
                        <PPErrorDisplay error={error} />
                    </div>
                </div>
            ) : null}
            {querydata?.stats?.filterDepartment && (
                <div className="mb-3">
                    <PPDefaultSelector
                        id="selectStatsFilterDepartment"
                        data={categorieTree}
                        onChange={(evt) => {
                            setSelectedDepartment(evt)
                        }}
                        name="department"
                        placeholder={t("single.department")}
                        value={selectedDepartment}
                        isClearable={false}
                    />
                </div>
            )}
            <div className="row">
                <PPTripleBarStatsBox items={querydata?.stats?.shopStats} />
            </div>
            <div className="row">
                <PPDonutStatsBox
                    onOpenClick={() => showMoreClick("lowerPrices")}
                    value={donutLower?.toFixed(2)}
                    valueAll={donutLowerAll?.toFixed(2)}
                    text={t(
                        isTotalPriceComparison
                            ? "dashboard.PPDonutStatsBox_lowerPricesTotal"
                            : "dashboard.PPDonutStatsBox_lowerPrices"
                    )}
                    startColor={PPUtils.Colors.GreenStart}
                    endColor={PPUtils.Colors.GreenEnd}
                    showMore={lowerPricesOpen}
                />
                <PPDonutStatsBox
                    onOpenClick={() => showMoreClick("equalsPrices")}
                    value={donutEqual?.toFixed(2)}
                    valueAll={donutEqualAll?.toFixed(2)}
                    text={t(
                        isTotalPriceComparison
                            ? "dashboard.PPDonutStatsBox_equalsPricesTotal"
                            : "dashboard.PPDonutStatsBox_equalsPrices"
                    )}
                    startColor={PPUtils.Colors.YellowStart}
                    endColor={PPUtils.Colors.YellowEnd}
                    showMore={equalPricesOpen}
                />
                <PPDonutStatsBox
                    onOpenClick={() => showMoreClick("higherPrices")}
                    value={donutHigher?.toFixed(2)}
                    valueAll={donutHigherAll?.toFixed(2)}
                    text={t(
                        isTotalPriceComparison
                            ? "dashboard.PPDonutStatsBox_higherPricesTotal"
                            : "dashboard.PPDonutStatsBox_higherPrices"
                    )}
                    startColor={PPUtils.Colors.RedStart}
                    endColor={PPUtils.Colors.RedEnd}
                    showMore={higherPricesOpen}
                />
            </div>

            <Collapse in={lowerPricesOpen || equalPricesOpen || higherPricesOpen}>
                <div className="card shadow card-primary card-outline card-outline-tabs">
                    <div className="card-header p-0 border-bottom-0">
                        <ul className="nav nav-tabs" id="custom-tabs-four-tab" role="tablist">
                            <li className="nav-item">
                                <a
                                    className="nav-link active"
                                    id="custom-tabs-four-price1-tab"
                                    data-bs-toggle="pill"
                                    href="#custom-tabs-four-price1"
                                    role="tab"
                                    onClick={() => window.dispatchEvent(new Event("resize"))}
                                    aria-controls="custom-tabs-four-stat1"
                                    aria-selected="true">
                                    {t("single.categories")}
                                </a>
                            </li>
                            <li className="nav-item">
                                <a
                                    className="nav-link"
                                    id="custom-tabs-four-price2-tab"
                                    data-bs-toggle="pill"
                                    href="#custom-tabs-four-price2"
                                    role="tab"
                                    onClick={() => window.dispatchEvent(new Event("resize"))}
                                    aria-controls="custom-tabs-four-stat2"
                                    aria-selected="false">
                                    {PPUtils.isTariffComparisonMode(tariffComparisonMode)
                                        ? PPUtils.getTariffComparisonModeProducerString(t)
                                        : t("single.manufacturer")}
                                </a>
                            </li>
                            <li className="nav-item">
                                <a
                                    className="nav-link"
                                    id="custom-tabs-four-price3-tab"
                                    data-bs-toggle="pill"
                                    href="#custom-tabs-four-price3"
                                    role="tab"
                                    onClick={() => window.dispatchEvent(new Event("resize"))}
                                    aria-controls="custom-tabs-four-stat3"
                                    aria-selected="false">
                                    Shops
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div className="card-body">
                        <div className="tab-content" id="custom-tabs-four-tabContent">
                            <div
                                className="tab-pane fade active show"
                                id="custom-tabs-four-price1"
                                role="tabpanel"
                                aria-labelledby="custom-tabs-four-price1-tab">
                                <DetailStatsPrices
                                    items={lowerByCategory}
                                    collapseBoxId={"productsPriceLower-show-more"}
                                    open={lowerPricesOpen}
                                    sortProperty={sortLower}
                                    tabName={TAB_NAME_KATEGORIE}
                                    searchName={"Kategorie"}
                                />
                                <DetailStatsPrices
                                    items={equalByCategory}
                                    collapseBoxId={"productsPriceEqual-show-more"}
                                    open={equalPricesOpen}
                                    sortProperty={sortEqual}
                                    tabName={TAB_NAME_KATEGORIE}
                                    searchName={"Kategorie"}
                                />
                                <DetailStatsPrices
                                    items={higherByCategory}
                                    collapseBoxId={"productsPriceHigher-show-more"}
                                    open={higherPricesOpen}
                                    sortProperty={sortHigher}
                                    tabName={TAB_NAME_KATEGORIE}
                                    searchName={"Kategorie"}
                                />
                            </div>
                            <div
                                className="tab-pane fade"
                                id="custom-tabs-four-price2"
                                role="tabpanel"
                                aria-labelledby="custom-tabs-four-price2-tab">
                                <DetailStatsPrices
                                    items={lowerByProducer}
                                    collapseBoxId={"productsPriceLower-show-more"}
                                    open={lowerPricesOpen}
                                    sortProperty={sortLower}
                                    tabName={TAB_NAME_HERSTELLER}
                                    searchName={"Hersteller"}
                                    tariffComparisonMode={tariffComparisonMode}
                                />
                                <DetailStatsPrices
                                    items={equalByProducer}
                                    collapseBoxId={"productsPriceEqual-show-more"}
                                    open={equalPricesOpen}
                                    sortProperty={sortEqual}
                                    tabName={TAB_NAME_HERSTELLER}
                                    searchName={"Hersteller"}
                                    tariffComparisonMode={tariffComparisonMode}
                                />
                                <DetailStatsPrices
                                    items={higherByProducer}
                                    collapseBoxId={"productsPriceHigher-show-more"}
                                    open={higherPricesOpen}
                                    sortProperty={sortHigher}
                                    tabName={TAB_NAME_HERSTELLER}
                                    searchName={"Hersteller"}
                                    tariffComparisonMode={tariffComparisonMode}
                                />
                            </div>
                            <div
                                className="tab-pane fade"
                                id="custom-tabs-four-price3"
                                role="tabpanel"
                                aria-labelledby="custom-tabs-four-price3-tab">
                                <DetailStatsPrices
                                    items={lowerByShops}
                                    collapseBoxId={"productsPriceLower-show-more"}
                                    open={lowerPricesOpen}
                                    sortProperty={sortLower}
                                    tabName={TAB_NAME_SHOP}
                                    searchName={"Shop"}
                                />
                                <DetailStatsPrices
                                    items={equalByShops}
                                    collapseBoxId={"productsPriceEqual-show-more"}
                                    open={equalPricesOpen}
                                    sortProperty={sortEqual}
                                    tabName={TAB_NAME_SHOP}
                                    searchName={"Shop"}
                                />
                                <DetailStatsPrices
                                    items={higherByShops}
                                    collapseBoxId={"productsPriceHigher-show-more"}
                                    open={higherPricesOpen}
                                    sortProperty={sortHigher}
                                    tabName={TAB_NAME_SHOP}
                                    searchName={"Shop"}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Collapse>

            <div className="row">
                <PPTopProducts
                    changeFilterDepartment={selectedDepartment}
                    tariffComparisonMode={tariffComparisonMode}
                    customerActiveShops={querydata?.settings?.customerActiveShops || EMPTY_SHOPS}
                />
            </div>

            <div className="row">
                <PPPriceTrendStatistics
                    changeFilterDepartment={selectedDepartment}
                    customerActiveShops={querydata?.settings?.customerActiveShops || EMPTY_SHOPS}
                />
            </div>

            <div className="row">
                <PPStatTableCountPriceLower
                    changeFilterDepartment={selectedDepartment}
                    tariffComparisonMode={tariffComparisonMode}
                />
            </div>

            <div className="row">
                <div className="col-lg col">
                    <PPStatsBox
                        value={querydata?.stats?.competitorWithMostPriceChanges
                            ?.replaceAll('"', "")
                            .replaceAll("\\", "")}
                        text={t("dashboard.PPStatsBox_topCompetitorPriceChanges")}
                        cssClass={"pp-stats-box"}
                        className={"topCompetitorPriceChanges"}
                        icon={"fa-clipboard"}
                        collapseBoxId={"topCompetitorPriceChanges-show-more"}
                        onChildClick={showMoreClick}
                        showMore={!competitorStatsMostPriceChangesOpen}
                    />
                </div>
                <div className="col-lg col">
                    <PPStatsBox
                        value={querydata?.stats?.competitorWithLowestPrices?.replaceAll('"', "").replaceAll("\\", "")}
                        text={t("dashboard.PPStatsBox_topCompetitorLowestPrice")}
                        cssClass={"pp-stats-box"}
                        className={"topCompetitorLowestPrice"}
                        icon={"fa-chart-line"}
                        collapseBoxId={"topCompetitorLowestPrice-show-more"}
                        onChildClick={showMoreClick}
                        showMore={!competitorStatsLowestPriceOpen}
                    />
                </div>
            </div>
            {querydata && (
                <DetailStatsCompetitorMostPriceChanges
                    items={querydata?.stats?.shopStats}
                    collapseBoxId={"topCompetitorPriceChanges-show-more"}
                    open={competitorStatsMostPriceChangesOpen}
                />
            )}
            {querydata && (
                <DetailStatsCompetitorLowestPrice
                    items={querydata?.stats?.competitorsWithLowestPrice}
                    collapseBoxId={"topCompetitorLowestPrice-show-more"}
                    open={competitorStatsLowestPriceOpen}
                />
            )}
            <div className="row">
                <div className="col-lg col">
                    <PPStatsBox
                        // value={querydata?.stats?.activeShops}
                        value={querydataShopInfos?.shops?.length}
                        text={t("dashboard.PPStatsBox_activeShops")}
                        cssClass={"pp-stats-box"}
                        className={"activeShops"}
                        icon={"fa-eye"}
                        collapseBoxId={"activeShops-show-more"}
                        onChildClick={showMoreClick}
                        showMore={!shopStatsOpen}
                    />
                </div>
                <div className="col-lg col">
                    <PPStatsBox
                        value={querydata?.stats?.crawlsToday}
                        text={t("dashboard.PPStatsBox_dailyCrawls")}
                        cssClass={"pp-stats-box"}
                        className={"dailyCrawls"}
                        icon={"fa-fish"}
                        collapseBoxId={"crawlsToday-show-more"}
                        onChildClick={showMoreClick}
                        showMore={false}
                        hasNoShowMore={true}
                    />
                </div>
            </div>
            {querydataShopInfos && (
                <DetailStatsShops
                    items={querydataShopInfos?.shops}
                    collapseBoxId={"activeShops-show-more"}
                    open={shopStatsOpen}
                />
            )}
            <div className="row">
                <div className="col ">
                    <div className="card shadow">
                        {querydata && (
                            <ShopFilterMappingHistoryChart title={t("dashboard.mapping_histoy")} data={querydata} />
                        )}
                    </div>
                </div>
            </div>
            <div
                className="modal fade"
                id="demoNoteModalCenter"
                tabIndex={-1}
                role="dialog"
                aria-labelledby="Demo Hinweis"
                aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="demoNoteModalLongTitle">
                            {t("demo_mode.welcome")}
                            </h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
                        </div>
                        <div className="modal-body ">
                            <p>{t("demo_mode.txt1")}</p>
                            <p>{t("demo_mode.txt2")}</p>
                            <ul>
                                <li>{t("demo_mode.txt3")}</li>
                                <li>{t("demo_mode.txt4")}</li>
                            </ul>
                            <p dangerouslySetInnerHTML={{ __html: t("demo_mode.txt5") }} />
                        
                            <div className="text-end">
                                <button
                                    className="btn btn-primary"
                                    style={{ minWidth: "100px" }}
                                    type="button"
                                    onClick={() => closeDemoNoteModal()}>
                                    {t("single.close")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PPDashboard
